import { DotsSixVerticalIcon, TrashIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { Editor } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface BlockHandleProps {
  editor: Editor;
}

/**
 * Unified drag/delete handle for all blocks in the editor.
 * Always appears in the left gutter. For blocks inside columns,
 * targets the individual block but positions horizontally based on
 * the column-layout wrapper so it stays in the gutter.
 */
const BlockHandle: React.FC<BlockHandleProps> = ({ editor }) => {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const blockInfoRef = useRef<{ pos: number } | null>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create a portal container and append to the EditorWrapper
  useEffect(() => {
    if (!editor?.view) return;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '0';
    container.style.height = '0';
    container.style.overflow = 'visible';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '20';
    container.classList.add('block-handle-portal');

    const editorWrapper = editor.view.dom.closest('.EditorWrapper');
    if (editorWrapper) {
      editorWrapper.appendChild(container);
    }
    portalContainerRef.current = container;

    return () => {
      container.remove();
      portalContainerRef.current = null;
    };
  }, [editor]);

  /**
   * Walk up from a DOM element to find the nearest handleable block.
   * Returns the block element and whether it's inside a column.
   */
  const findHandleableBlock = useCallback(
    (
      target: HTMLElement,
    ): { el: HTMLElement; insideColumn: boolean } | null => {
      let el: HTMLElement | null = target;
      const proseMirror = editor?.view?.dom;
      if (!proseMirror) return null;

      while (el && el !== proseMirror) {
        if (!el.parentElement) break;

        // Block inside a column
        if (el.parentElement.classList?.contains('column')) {
          return { el, insideColumn: true };
        }

        // Direct child of ProseMirror (top-level block)
        if (el.parentElement === proseMirror) {
          // Skip column-layout — we handle its children individually
          if (el.classList?.contains('column-layout')) {
            return null;
          }
          return { el, insideColumn: false };
        }

        el = el.parentElement;
      }
      return null;
    },
    [editor],
  );

  const updatePosition = useCallback(
    (blockEl: HTMLElement, insideColumn: boolean) => {
      const editorWrapper = editor?.view?.dom?.closest('.EditorWrapper');
      const editorRect = editorWrapper?.getBoundingClientRect();
      if (!editorRect) return;

      const blockRect = blockEl.getBoundingClientRect();
      const scrollTop = editorWrapper!.scrollTop;

      if (insideColumn) {
        // Inside a column: position handle to the left of the block.
        // For left-column blocks this lands in the left gutter;
        // for right-column blocks it lands in the 60px gap between columns.
        setPosition({
          top: blockRect.top - editorRect.top + scrollTop,
          left: blockRect.left - editorRect.left - 52,
        });
      } else {
        // Top-level block: position in the left gutter
        setPosition({
          top: blockRect.top - editorRect.top + scrollTop,
          left: blockRect.left - editorRect.left - 52,
        });
      }
    },
    [editor],
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!editor?.view) return;

      const target = event.target as HTMLElement;

      // Don't reprocess if hovering over the handle itself
      if (handleRef.current?.contains(target)) return;

      // Check if we're over the ProseMirror editor content
      const proseMirror = editor.view.dom;
      if (!proseMirror.contains(target)) {
        // Mouse is in the EditorWrapper but not over editor content (the gap).
        // Don't hide — let the user traverse to the handle.
        return;
      }

      const blockResult = findHandleableBlock(target);
      if (!blockResult) {
        if (!hideTimeoutRef.current) {
          hideTimeoutRef.current = setTimeout(() => {
            setPosition(null);
            blockInfoRef.current = null;
            hideTimeoutRef.current = null;
          }, 300);
        }
        return;
      }

      // Cancel any pending hide
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      const { el: blockEl, insideColumn } = blockResult;

      // Resolve ProseMirror position
      const pos = editor.view.posAtDOM(blockEl, 0);
      if (pos < 0) return;

      const $pos = editor.state.doc.resolve(pos);
      let blockPos = pos;

      if (insideColumn) {
        // Inside a column — find the block that is a direct child of the column
        for (let depth = $pos.depth; depth >= 1; depth--) {
          if ($pos.node(depth - 1).type.name === 'column') {
            blockPos = $pos.before(depth);
            break;
          }
        }
      } else {
        // Top-level block — find direct child of doc
        for (let depth = $pos.depth; depth >= 1; depth--) {
          if ($pos.node(depth - 1).type.name === 'doc') {
            blockPos = $pos.before(depth);
            break;
          }
        }
      }

      blockInfoRef.current = { pos: blockPos };
      updatePosition(blockEl, insideColumn);
    },
    [editor, findHandleableBlock, updatePosition],
  );

  const handleEditorLeave = useCallback((event: MouseEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (handleRef.current?.contains(relatedTarget)) return;
    if (!hideTimeoutRef.current) {
      hideTimeoutRef.current = setTimeout(() => {
        setPosition(null);
        blockInfoRef.current = null;
        hideTimeoutRef.current = null;
      }, 300);
    }
  }, []);

  useEffect(() => {
    if (!editor?.view) return;

    // Listen on EditorWrapper so the gap between blocks and handle is covered
    const editorWrapper = editor.view.dom.closest('.EditorWrapper');
    if (!editorWrapper) return;

    editorWrapper.addEventListener(
      'mousemove',
      handleMouseMove as EventListener,
    );
    editorWrapper.addEventListener(
      'mouseleave',
      handleEditorLeave as EventListener,
    );

    return () => {
      editorWrapper.removeEventListener(
        'mousemove',
        handleMouseMove as EventListener,
      );
      editorWrapper.removeEventListener(
        'mouseleave',
        handleEditorLeave as EventListener,
      );
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [editor, handleMouseMove, handleEditorLeave]);

  const handleDelete = useCallback(() => {
    if (!blockInfoRef.current || !editor) return;

    const { pos } = blockInfoRef.current;
    const node = editor.state.doc.nodeAt(pos);
    if (!node) return;

    editor.commands.deleteRange({
      from: pos,
      to: pos + node.nodeSize,
    });

    setPosition(null);
    blockInfoRef.current = null;
  }, [editor]);

  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      if (!blockInfoRef.current || !editor) return;

      const { pos } = blockInfoRef.current;
      const node = editor.state.doc.nodeAt(pos);
      if (!node) return;

      const { view } = editor;

      // Set a NodeSelection on the block so ProseMirror's built-in drop
      // handler knows what to delete when performing a move
      view.dispatch(
        view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)),
      );

      const slice = view.state.doc.slice(pos, pos + node.nodeSize);

      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', '');

      view.dragging = { slice, move: true };
    },
    [editor],
  );

  const handleHandleMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleHandleMouseLeave = useCallback(() => {
    if (!hideTimeoutRef.current) {
      hideTimeoutRef.current = setTimeout(() => {
        setPosition(null);
        blockInfoRef.current = null;
        hideTimeoutRef.current = null;
      }, 300);
    }
  }, []);

  if (!portalContainerRef.current) return null;

  return createPortal(
    position ? (
      <HandleContainer
        ref={handleRef}
        style={{
          top: position.top,
          left: position.left,
          pointerEvents: 'auto',
        }}
        onMouseEnter={handleHandleMouseEnter}
        onMouseLeave={handleHandleMouseLeave}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <HandleButton onClick={handleDelete} aria-label="Delete">
              <TrashIcon size={19} />
            </HandleButton>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="text-xs"
            style={{
              backgroundColor: 'hsl(var(--primary))',
              borderColor: 'hsl(var(--primary))',
            }}
          >
            <span className="font-semibold">Delete</span> this block
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <HandleButton
              draggable
              onDragStart={handleDragStart}
              aria-label="Drag to move"
              style={{ cursor: 'grab' }}
            >
              <DotsSixVerticalIcon size={19} weight="bold" />
            </HandleButton>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="text-xs"
            style={{
              backgroundColor: 'hsl(var(--primary))',
              borderColor: 'hsl(var(--primary))',
            }}
          >
            <span className="font-semibold">Drag</span> to move
          </TooltipContent>
        </Tooltip>
      </HandleContainer>
    ) : null,
    portalContainerRef.current,
  );
};

export default BlockHandle;

const HandleContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 12px 4px 4px;
`;

const HandleButton = styled.button`
  padding: 0.25px;
  border-radius: 4px;
  border: none;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  &:hover {
    background-color: #e8e8e8;
    color: hsl(var(--primary));
  }
`;
